/**
 * Design System Tokens for Atlas V2
 * Based on Figma design specifications
 */

export const colors = {
  // Status Colors
  status: {
    positive: '#169959',
    neutral: '#D7DADB',
    negative: '#C32928',
    warning: '#E6B339',
    tertiary: '#179DB5', // Tertiary teal for status pills
  },
  
  // Text Colors
  text: {
    positive: {
      onPositive: '#EBFFEE',
    },
    neutral: {
      onNeutral: '#F3F3F3',
    },
    danger: {
      onDanger: '#FEE9E7',
    },
    warning: {
      onWarning: '#401B01',
    },
    primary: '#333333',
    secondary: '#666666',
    tertiary: '#999999',
  },
  
  // Background Colors
  background: {
    primary: '#FFFFFF',
    secondary: '#F5F5F5',
    tertiary: '#EEEEEE',
  },
  
  // Border Colors
  border: {
    default: '#E0E0E0',
    hover: '#CCCCCC',
    focus: '#179DB5',
  },
  
  // Brand Colors (from legacy design system)
  primary: {
    40: '#94B2B8', // Light teal
    60: '#496E74', // Medium teal
  },
  
  // Additional color tokens
  tertiary: '#179DB5', // Accent teal
  secondary: '#F5F5F5', // Secondary background
  
  // Neutral color variations
  neutral: {
    40: '#A9B1B2', // Medium neutral for appointments
  },
} as const;

export const typography = {
  fontFamily: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    redditSans: '"Reddit Sans", sans-serif',
    redditSansCondensed: '"Reddit Sans Condensed", sans-serif',
    redditMono: '"Reddit Mono", monospace',
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeight: {
    none: 0,
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  // Semantic typography variants based on Figma design system
  variants: {
    // Headers
    title: {
      fontFamily: '"Reddit Sans Condensed", sans-serif',
      fontWeight: 800, // ExtraBold
      fontSize: '64px',
      lineHeight: 1.0,
      letterSpacing: '2.56px',
    },
    h1: {
      fontFamily: '"Reddit Sans Condensed", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '32px',
      lineHeight: 1.1,
      letterSpacing: '0.64px',
    },
    h2: {
      fontFamily: '"Reddit Sans Condensed", sans-serif',
      fontWeight: 600, // SemiBold  
      fontSize: '24px',
      lineHeight: 1.2,
      letterSpacing: '0.48px',
    },
    h3: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 800, // ExtraBold
      fontSize: '20px', 
      lineHeight: 1.2,
      letterSpacing: '0.4px',
    },
    
    // Body text
    trackingStatus: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '16px',
      lineHeight: 1.3,
      letterSpacing: '0.32px',
    },
    label: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '0.28px',
    },
    valueAddress: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '0.28px',
    },
    
    // Status pill (existing pattern)
    statusPill: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: 500, // Medium
      fontSize: '12px',
      lineHeight: 1,
      letterSpacing: 'normal',
    },
    
    // Button text
    button: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: 500, // Medium
      lineHeight: 1,
      letterSpacing: 'normal',
    },
    
    // Card content
    cardTitle: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '18px',
      lineHeight: 1.2,
      letterSpacing: '0.36px',
    },
    cardSubtitle: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 600, // SemiBold
      fontSize: '16px',
      lineHeight: 1.2,
      letterSpacing: '0.32px',
    },
    cardBody: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '0.28px',
    },
    cardCaption: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '12px',
      lineHeight: 1.3,
      letterSpacing: '0.24px',
    },
    
    // Ledger/Financial text styles
    ledgerLabel: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '0.28px',
      textTransform: 'uppercase' as const,
    },
    ledgerNumbers: {
      fontFamily: '"Reddit Mono", monospace',
      fontWeight: 400, // Regular
      fontSize: '13px',
      lineHeight: 1.3,
      letterSpacing: '-0.26px',
    },
    
    // Value text (data display)
    value: {
      fontFamily: '"Reddit Sans", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '0.28px',
    },
  },
} as const;

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  // Figma-specific spacing values
  figma: {
    sectionGap: '18px',      // Gap between sections in cards
    cardPaddingX: '42px',    // Horizontal card padding 
    cardPaddingY: '36px',    // Vertical card padding
  },
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
} as const;

export const borderRadius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const;

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
} as const;